import Echo from 'laravel-echo';
import Pusher, { type ChannelAuthorizationCallback } from 'pusher-js';
import { api } from './api';

/**
 * Lazily-built Laravel Echo client pointed at Reverb. Returns null when Reverb
 * isn't configured (no VITE_REVERB_APP_KEY) so callers degrade to polling.
 *
 * Channel auth reuses the axios `api` instance, so the Sanctum session cookie +
 * XSRF header travel with the /broadcasting/auth request on the API host.
 */
let echo: Echo<'reverb'> | null | undefined;

export function getEcho(): Echo<'reverb'> | null {
  if (echo !== undefined) {
    return echo;
  }

  const key = import.meta.env.VITE_REVERB_APP_KEY as string | undefined;
  if (!key) {
    echo = null;
    return null;
  }

  (window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

  const host = (import.meta.env.VITE_REVERB_HOST as string) || window.location.hostname;
  const port = Number(import.meta.env.VITE_REVERB_PORT ?? 8080);
  const scheme = (import.meta.env.VITE_REVERB_SCHEME as string) || 'http';

  try {
    echo = new Echo({
      broadcaster: 'reverb',
      key,
      wsHost: host,
      wsPort: port,
      wssPort: port,
      forceTLS: scheme === 'https',
      enabledTransports: ['ws', 'wss'],
      authorizer: (channel: { name: string }) => ({
        authorize(socketId: string, callback: ChannelAuthorizationCallback) {
          api
            .post('/broadcasting/auth', {
              socket_id: socketId,
              channel_name: channel.name,
            })
            .then((res) => callback(null, res.data))
            .catch((err) =>
              callback(err instanceof Error ? err : new Error('Broadcast auth failed'), null),
            );
        },
      }),
    });
  } catch {
    echo = null;
  }

  return echo;
}
